/**
 * SharedWorker 클라이언트 래퍼.
 * 기존 SocketManager와 동일한 API(subscribe, unsubscribe, onConnectionChange)를 제공하며,
 * 내부적으로 SharedWorker의 MessagePort를 통해 통신한다.
 * SharedWorker 미지원 시 기존 SocketManager로 fallback.
 */

import { SocketManager } from './SocketManager';
import type { WorkerInboundMessage, WorkerOutboundMessage, SubscribeFormat } from './worker-messages';

type WebSocketData = string | ArrayBuffer | Blob;

interface SharedWorkerClientOptions {
  exchange: string;
  url: string;
  buildSubscribeMessage: (type: string, codes: string[]) => unknown;
  subscribeFormat?: SubscribeFormat;
  pingInterval?: number;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

interface Subscriber {
  id: string;
  codes: string[];
  type: string;
  onMessage: (data: WebSocketData) => void | Promise<void>;
}

let subscriberIdCounter = 0;
let sharedWorkerInstance: SharedWorker | null = null;
let sharedWorkerFailed = false;
let portInitialized = false;

// 🔴 TRAD-3045-heartbeat — 이 페이지(물리 탭) 전체가 공유하는 tabId. 모듈 레벨에서 딱 한 번
// 만든다. 예전엔 exchange별 SharedWorkerSocketClient 인스턴스가 각자 자기 tabId를 새로
// 찍었는데, 워커의 dead-tab 정리(heartbeat_ping/pong, HEARTBEAT_TIMEOUT)는 tabId 단위로
// 생사를 판정한다 — "tabId 1개 = 물리 탭 1개"라는 워커 쪽 모델과 어긋난 것이다.
// heartbeat_ping에는 "첫 번째로 등록된 client" 하나만 응답하므로(ensurePortDispatcher),
// 거래소마다 tabId가 다르면 두 번째 이후 거래소의 tabId는 pong을 영원히 못 받고 35초 뒤
// "죽은 탭"으로 오인 제거돼 그 거래소의 구독이 통째로 사라졌다(실측: 빗썸 실시간 시세가
// 정지 30~65초 뒤 이유 없이 끊기던 버그의 근본 원인). tabId를 페이지당 하나로 공유하면
// 어느 client가 응답하든 같은 tabId로 나가므로 모든 거래소의 생존이 함께 갱신된다.
const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** exchange → SharedWorkerSocketClient 매핑. 단일 port의 메시지를 각 클라이언트로 라우팅한다. */
const clientRegistry = new Map<string, SharedWorkerSocketClient>();

function getSharedWorker(): SharedWorker | null {
  if (sharedWorkerFailed) return null;
  if (sharedWorkerInstance) return sharedWorkerInstance;

  try {
    sharedWorkerInstance = new SharedWorker(new URL('./shared-worker.ts', import.meta.url), {
      type: 'module',
      name: 'xtrading-socket-worker',
    });
    return sharedWorkerInstance;
  } catch {
    sharedWorkerFailed = true;
    return null;
  }
}

/**
 * SharedWorker port에 단일 onmessage 핸들러를 등록하고,
 * exchange 필드 기반으로 등록된 클라이언트에 메시지를 라우팅한다.
 */
function ensurePortDispatcher(worker: SharedWorker) {
  if (portInitialized) return;
  portInitialized = true;

  worker.port.onmessage = (e: MessageEvent<WorkerOutboundMessage>) => {
    const msg = e.data;

    if (msg.type === 'heartbeat_ping') {
      // 🔴 TRAD-3045-heartbeat — "첫 번째 client 하나만 응답해도 되는" 이유: 모든 client가
      // 위 TAB_ID(페이지당 하나)를 공유하므로, 어느 client가 응답하든 워커 쪽 tabs 맵의
      // 같은 tabId 항목이 갱신돼 페이지 전체(모든 거래소)의 생존이 함께 확인된다.
      // (예전엔 exchange별로 tabId가 달라서 이 가정이 깨져 있었다 — TAB_ID 선언부 주석 참고.)
      const first = clientRegistry.values().next().value;
      if (first) {
        first.handleWorkerMessage(msg);
      }
      return;
    }

    if ('exchange' in msg) {
      const client = clientRegistry.get(msg.exchange);
      if (client) {
        client.handleWorkerMessage(msg);
      }
    }
  };

  worker.port.start();
}

/**
 * SharedWorker를 사용할 수 있는 환경인지 확인.
 * - SharedWorker API 존재 여부
 * - Worker 인스턴스 생성 가능 여부
 */
function isSharedWorkerSupported(): boolean {
  if (typeof SharedWorker === 'undefined') return false;
  return getSharedWorker() !== null;
}

/**
 * SharedWorker 기반 소켓 클라이언트.
 * SocketManager와 동일한 외부 API를 제공한다.
 */
class SharedWorkerSocketClient {
  private readonly exchange: string;
  private readonly tabId: string;
  private port: MessagePort;
  private subscribers = new Map<string, Subscriber>();
  private connectionListeners = new Set<(connected: boolean) => void>();
  private gaveUpListeners = new Set<() => void>();
  private _connected = false;
  private configSent = false;
  private readonly options: SharedWorkerClientOptions;

  constructor(options: SharedWorkerClientOptions, worker: SharedWorker) {
    this.exchange = options.exchange;
    this.tabId = TAB_ID; // 🔴 인스턴스(거래소)마다 새로 찍지 않는다 — 위 TAB_ID 주석 참고.
    this.options = options;
    this.port = worker.port;

    // 중앙 디스패처에 등록 (port.onmessage는 디스패처가 관리)
    clientRegistry.set(this.exchange, this);
    ensurePortDispatcher(worker);

    this.setupBeforeUnload();
  }

  private ensureConfig() {
    if (this.configSent) return;
    this.configSent = true;

    const msg: WorkerInboundMessage = {
      type: 'exchange_config',
      exchange: this.exchange,
      url: this.options.url,
      subscribeFormat: this.options.subscribeFormat ?? 'ticket',
      pingInterval: this.options.pingInterval ?? 30_000,
      reconnectInterval: this.options.reconnectInterval ?? 3_000,
      maxReconnectAttempts: this.options.maxReconnectAttempts ?? 5,
    };
    this.port.postMessage(msg);
  }

  handleWorkerMessage(msg: WorkerOutboundMessage) {
    switch (msg.type) {
      case 'ws_data':
        if (msg.exchange === this.exchange) {
          // Worker에서 string으로 보내므로 Blob으로 변환하여 기존 Hook 호환성 유지
          const blob = new Blob([msg.data], { type: 'application/json' });
          this.subscribers.forEach(sub => {
            void sub.onMessage(blob);
          });
        }
        break;

      case 'connection_change':
        if (msg.exchange === this.exchange) {
          this._connected = msg.connected;
          this.connectionListeners.forEach(fn => fn(msg.connected));
        }
        break;

      case 'heartbeat_ping':
        this.port.postMessage({
          type: 'heartbeat_pong',
          tabId: this.tabId,
        } satisfies WorkerInboundMessage);
        break;

      case 'connection_gave_up':
        if (msg.exchange === this.exchange) {
          this.gaveUpListeners.forEach(fn => fn());
        }
        break;
    }
  }

  private setupBeforeUnload() {
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.port.postMessage({
          type: 'disconnect',
          tabId: this.tabId,
        } satisfies WorkerInboundMessage);
      });
    }
  }

  subscribe(type: string, codes: string[], onMessage: (data: WebSocketData) => void | Promise<void>): () => void {
    this.ensureConfig();

    const id = `sub_${++subscriberIdCounter}`;
    this.subscribers.set(id, { id, codes, type, onMessage });

    // TRAD-웹소켓끊김 — 이 client 인스턴스는 exchange당 싱글턴이라, 같은 type을 구독하는
    // 로컬 구독자가 여러 명일 수 있다(예: 헤더 MarketSummaryStrip + 포트폴리오 페이지가
    // 둘 다 upbit 'ticker' 구독). 이 subscriber만의 codes를 보내면 worker에 (tabId, type)당
    // 여러 항목이 남았다가 unsubscribe 타이밍에 서로 지워버리는 문제가 있었다.
    // unsubscribe()가 이미 하던 것과 대칭으로, 매 subscribe도 같은 type 로컬 구독자
    // 전원의 codes를 합쳐 보낸다 — worker는 (tabId, type)당 정확히 한 항목만 유지한다.
    this.sendMergedSubscribe(type);

    return () => this.unsubscribe(id, type);
  }

  private sendMergedSubscribe(type: string) {
    const mergedCodes = new Set<string>();
    this.subscribers.forEach(sub => {
      if (sub.type === type) {
        sub.codes.forEach(c => mergedCodes.add(c));
      }
    });

    this.port.postMessage({
      type: 'subscribe',
      tabId: this.tabId,
      exchange: this.exchange,
      subscriptionType: type,
      codes: [...mergedCodes],
    } satisfies WorkerInboundMessage);
  }

  private unsubscribe(id: string, type: string) {
    this.subscribers.delete(id);

    // 같은 type의 다른 구독자가 있는지 확인
    const hasOtherSameType = [...this.subscribers.values()].some(s => s.type === type);

    if (!hasOtherSameType) {
      this.port.postMessage({
        type: 'unsubscribe',
        tabId: this.tabId,
        exchange: this.exchange,
        subscriptionType: type,
      } satisfies WorkerInboundMessage);
    } else {
      // 같은 type의 구독이 남아있으면 codes를 업데이트해야 하므로 재구독
      this.sendMergedSubscribe(type);
    }
  }

  get connected(): boolean {
    return this._connected;
  }

  onConnectionChange(listener: (connected: boolean) => void): () => void {
    this.connectionListeners.add(listener);
    return () => this.connectionListeners.delete(listener);
  }

  /** TRAD-3045 — 재연결을 다 쓰고 포기했을 때 알림. */
  onGaveUp(listener: () => void): () => void {
    this.gaveUpListeners.add(listener);
    return () => this.gaveUpListeners.delete(listener);
  }

  /** TRAD-3045 — 사용자가 "다시 시도"를 눌렀을 때 호출한다. */
  retry() {
    this.port.postMessage({
      type: 'retry_connection',
      exchange: this.exchange,
    } satisfies WorkerInboundMessage);
  }

  destroy() {
    this.subscribers.clear();
    this.connectionListeners.clear();
    this.gaveUpListeners.clear();
    clientRegistry.delete(this.exchange);
    this.port.postMessage({
      type: 'disconnect',
      tabId: this.tabId,
    } satisfies WorkerInboundMessage);
  }
}

// --- 팩토리 ---

/** subscribe/onConnectionChange/onGaveUp/retry/connected/destroy를 가진 공통 인터페이스 */
export interface SocketClientLike {
  subscribe(type: string, codes: string[], onMessage: (data: WebSocketData) => void | Promise<void>): () => void;
  onConnectionChange(listener: (connected: boolean) => void): () => void;
  /** TRAD-3045 — 재연결을 다 쓰고 포기했을 때 알림. */
  onGaveUp(listener: () => void): () => void;
  /** TRAD-3045 — 사용자가 "다시 시도"를 눌렀을 때 호출한다. */
  retry(): void;
  readonly connected: boolean;
  destroy(): void;
}

export type SocketClient = SocketManager | SharedWorkerSocketClient;

interface CreateSocketClientOptions extends SharedWorkerClientOptions {
  /** SharedWorker 미지원 시 사용할 커스텀 fallback 클라이언트 */
  fallbackClient?: SocketClientLike;
}

/**
 * SharedWorker를 지원하면 SharedWorkerSocketClient를, 아니면 SocketManager(또는 fallback)를 반환.
 */
export function createSocketClient(options: CreateSocketClientOptions): SocketClientLike {
  if (isSharedWorkerSupported()) {
    const worker = getSharedWorker()!;
    return new SharedWorkerSocketClient(options, worker);
  }

  // 커스텀 fallback이 제공되면 사용 (STOMP 등)
  if (options.fallbackClient) {
    return options.fallbackClient;
  }

  // Fallback: 기존 SocketManager
  return new SocketManager({
    url: options.url,
    buildSubscribeMessage: options.buildSubscribeMessage,
    // TRAD-3635 — 코인원은 폴백에서도 자기 포맷(종목별 요청 + JSON PING)을 쓴다. 다른 포맷은 종전 동작.
    subscribeFormat: options.subscribeFormat === 'coinone' ? 'coinone' : undefined,
    pingInterval: options.pingInterval,
    reconnectInterval: options.reconnectInterval,
    maxReconnectAttempts: options.maxReconnectAttempts,
  });
}
